import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import scaylorOrb from '../assets/images/ScaylorOrbB.svg';
import curvedWisp from '../assets/images/Background Curve Wisp.png';
import bluePinkGradient from '../assets/images/bluePnkGradient.svg';
import pinkRedGradient from '../assets/images/pinkRedGradient.svg';
import { US_STATES } from '../utils/states';
import { TIMEZONES } from '../utils/timezones';

const OrganizationSetup = () => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState('United States');
  const [selectedTimezone, setSelectedTimezone] = useState('America/New_York');
  const [selectedState, setSelectedState] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    address: '',
    city: '',
    zipCode: '',
    phone: '',
  });

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
  };

  const handleTimezoneChange = (event) => {
    setSelectedTimezone(event.target.value);
  };

  const handleStateChange = (event) => {
    setSelectedState(event.target.value);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    // Validate all fields are filled
    const requiredFields = {
      name: 'Organization Name',
      website: 'Website',
      address: 'Address',
      city: 'City',
      zipCode: 'Zip Code',
      phone: 'Phone'
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (!formData[field]) {
        alert(`${label} is required`);
        return;
      }
    }

    if (!selectedState) {
      alert('State is required');
      return;
    }

    // Construct the full address
    const fullAddress = `${formData.address}, ${formData.city}, ${selectedState} ${formData.zipCode}`;

    const organizationData = {
      name: formData.name,
      country: selectedCountry,
      timezone: selectedTimezone,
      address: fullAddress,
      phone: formData.phone,
      website: formData.website
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/organization/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        body: JSON.stringify(organizationData),
      });

      if (response.ok) {
        navigate('/'); // Navigate to home page after successful setup
      } else {
        const error = await response.json();
        alert('Failed to setup organization: ' + (error.message || 'Unknown error'));
      }
    } catch (error) {
      alert('Network error: ' + error.message);
    }
  };

  return (
    <div className="h-screen flex items-start justify-center pt-24 overflow-hidden relative">
      {/* Background Elements */}
      <div className="absolute bottom-0 left-0">
        <img 
          src={bluePinkGradient} 
          alt="Blue Pink Gradient" 
          className="w-[800px] opacity-500"
        />
      </div>

      <div className="absolute top-0 left-0">
        <img 
          src={curvedWisp} 
          alt="Background Wisp" 
          className="w-[300px] opacity-100"
        />
      </div>

      <div className="absolute top-0 right-0">
        <img 
          src={pinkRedGradient} 
          alt="Pink Red Gradient" 
          className="w-[700px] opacity-100"
        />
      </div>

      <div className="absolute inset-0 flex items-start justify-center pt-[4%]">
        <img 
          src={scaylorOrb} 
          alt="Scaylor Orb" 
          className="w-[800px] opacity-50"
        />
      </div>

      {/* Organization Setup Form */}
      <div className="bg-white/10 backdrop-blur-md rounded-3xl p-12 w-[900px] shadow-lg z-10 relative mt-[2%]">
        <div className="flex flex-col items-center mb-8">
          <h1 className="text-7xl font-bold font-montserrat text-shadow-custom mb-4">SCAYLOR</h1>
          <h2 className="text-2xl font-Gilroy font-bold">Set Up Your Organization</h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-8">
            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-2 font-Gilroy font-medium">Organization Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Enter organization name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm mb-2 font-Gilroy font-medium">Timezone</label>
                <select 
                  value={selectedTimezone}
                  onChange={handleTimezoneChange}
                  className="w-full p-2 border rounded-lg text-gray-600"
                  required
                >
                  {TIMEZONES.map(timezone => (
                    <option key={timezone.value} value={timezone.value}>
                      {timezone.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm mb-2 font-Gilroy font-medium">Website</label>
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Enter website URL"
                  required
                />
              </div>

              <div>
                <label className="block text-sm mb-2 font-Gilroy font-medium">Address</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Enter street address"
                  required
                />
              </div>

              <div>
                <label className="block text-sm mb-2 font-Gilroy font-medium">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Enter city"
                  required
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm mb-2 font-Gilroy font-medium">Country</label>
                <select 
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  className="w-full p-2 border rounded-lg text-gray-600"
                  required
                >
                  <option value="United States">United States</option>
                  <option value="Canada">Canada</option>
                  <option value="United Kingdom">United Kingdom</option>
                  <option value="Australia">Australia</option>
                  <option value="New Zealand">New Zealand</option>
                  <option value="South Africa">South Africa</option>
                  <option value="India">India</option>
                  <option value="China">China</option>
                  <option value="Japan">Japan</option>
                  <option value="Korea">Korea</option>
                </select>
              </div>

              <div>
                <label className="block text-sm mb-2 font-Gilroy font-medium">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Enter phone number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm mb-2 font-Gilroy font-medium">State</label>
                <select 
                  value={selectedState}
                  onChange={handleStateChange}
                  className="w-full p-2 border rounded-lg text-gray-600"
                  required
                >
                  <option value="">Select State</option>
                  {US_STATES.map(state => (
                    <option key={state.value} value={state.value}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm mb-2 font-Gilroy font-medium">Zip Code</label>
                <input
                  type="text"
                  name="zipCode"
                  value={formData.zipCode}
                  onChange={handleChange}
                  className="w-full p-2 border rounded-lg"
                  placeholder="Enter zip code"
                  required
                />
              </div>
            </div>
          </div>

          <div className="flex justify-center mt-8">
            <button
              type="submit"
              className="bg-black text-white px-8 py-3 rounded-lg hover:bg-gray-800 font-Gilroy font-bold"
            >
              Create Organization
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationSetup; 